import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Crown, Eye, EyeOff, Shield } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register, login2FA } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        // Login with potential 2FA
        const result = await login(formData.email, formData.password, requires2FA ? twoFactorCode : null);
        
        if (result.requires_2fa) {
          setRequires2FA(true);
          toast.info('Please enter your 2FA code');
          setLoading(false);
          return;
        }
        
        if (result.success) {
          toast.success('Welcome back!');
          navigate('/dashboard', { replace: true });
        } else {
          toast.error(result.error || 'Login failed');
        }
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        
        const result = await register(formData.email, formData.password, formData.name);
        if (result.success) {
          toast.success('Account created successfully!');
          navigate('/dashboard', { replace: true });
        } else {
          toast.error(result.error || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Crown className="h-10 w-10 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-bold text-white">Nouveau Riche Legacy</h1>
            <p className="text-gray-400 text-sm">Build Your Financial Dynasty</p>
          </Link>
        </div>

        <Card className="bg-[#14120f] border-[#1c1814]">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {isLogin 
                ? 'Sign in to access your legacy dashboard' 
                : 'Join the Nouveau Riche community'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name field (register only) */}
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 bg-[#0a0a0f] border-[#1c1814] text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-[#0a0a0f] border-[#1c1814] text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                    required
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isLogin ? 'Enter your password' : 'Create a password (min 6 chars)'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 bg-[#0a0a0f] border-[#1c1814] text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password field (register only) */}
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="pl-10 bg-[#0a0a0f] border-[#1c1814] text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {/* 2FA Code field (login only, when required) */}
              {isLogin && requires2FA && (
                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode" className="text-gray-300">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-[#D4AF37]" />
                      Two-Factor Authentication Code
                    </div>
                  </Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-[#0a0a0f] border-[#1c1814] text-white text-center text-2xl tracking-widest placeholder:text-gray-500 focus:border-[#D4AF37]"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500">Enter the code from your authenticator app</p>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-sm text-[#D4AF37] hover:underline"
                  >
                    ← Use different account
                  </button>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-semibold py-6"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {isLogin ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="h-5 w-5" />
                  </div>
                )}
              </Button>

              {/* Forgot Password link (login only) */}
              {isLogin && (
                <div className="text-center">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
            </form>

            {/* Toggle between login/register */}
            <div className="mt-6 text-center">
              <p className="text-gray-400">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setFormData({ email: '', password: '', name: '', confirmPassword: '' });
                  }}
                  className="ml-2 text-[#D4AF37] hover:text-[#B8962E] font-medium"
                >
                  {isLogin ? 'Create one' : 'Sign in'}
                </button>
              </p>
            </div>

            {/* Back to home */}
            <div className="mt-4 text-center">
              <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-4">What you'll get access to:</p>
          <div className="flex justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              Trading Terminal
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              Financial Education
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              Affiliate Program
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
